import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { ProjectState, ProjectItem } from "../../types/interface";
import { RootState } from "../store";
import { useSelector } from "react-redux";
import {
  fetchProjects,
  addProject as addProjectAPI,
  editProject as editProjectAPI,
  toggleLike as toggleLikeAPI,
} from "./projectHandler";
import {
  FAILED,
  LOADING,
  SUCCEEDED,
  IDLE,
  FAILED_TO_FETCH_PROJECTS,
  FAILED_TO_ADD_PROJECT,
  FAILED_TO_EDIT_PROJECT,
} from "../../constants";
import AxiosInstance from "../../Service/AxiosInstance";
import { AxiosProgressEvent } from "axios";

// Define the interface for the new task payload
export interface ProjectPayload {
  id: string;
  name: string;
  description: string;
  taskTypeId: string;
  domainTypeId: string;
}

const initialState: ProjectState = {
  projects: [],
  status: IDLE,
  error: null,
  loading: false,
};

// Create an async thunk for deleting a project
export const deleteProject = createAsyncThunk(
  "project/deleteProject",
  async (projectId: string, { rejectWithValue }) => {
    try {
      await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Project/DeleteProject?projectId=${projectId}`
      );
      return projectId; // Returning the projectId to remove it from the state
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateProject = createAsyncThunk(
  "project/updateProject",
  async (updatedData: any, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Project/Update`,
        updatedData // Only updatedData is sent as the payload
      );
      return response.data; // Return the updated project data if needed in the state
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const addFavourites = createAsyncThunk(
  "/api/Project/UpdateFavorites",
  async (updatedData: any, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Project/UpdateFavorites`,
        updatedData // Only updatedData is sent as the payload
      );
      return response.data; // Return the updated project data if needed in the state
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Create an async thunk for adding a new task
export const addProject = createAsyncThunk(
  "tasks/addProject",
  async (newProject: ProjectPayload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Project/Add`,
        newProject
      );
      return response.data; // Assuming the response data is the added Task
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const downloadProjectDataset = createAsyncThunk<
  { success: boolean; duration: number }, // return type
  {
    data: { projectId: string };
    options?: {
      onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void;
    };
  }, // argument type
  { rejectValue: string } // reject type
>(
  "project/downloadProjectDataset",
  async ({ data, options }, { rejectWithValue }) => {
    try {
      const startTime = Date.now();

      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectDownload/DownloadProject?projectId=${data.projectId}`,
        {
          responseType: "blob", // for binary data
          ...options, // merges onDownloadProgress if provided
        }
      );

      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.projectId}.zip`; // filename = projectId.zip
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      const endTime = Date.now();
      const duration = endTime - startTime;

      return { success: true, duration };
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const downloadSampleProject = createAsyncThunk(
  "/api/Project/AddSampleProject",
  async ({ data }: { data: any }, { rejectWithValue, getState }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Project/AddSampleProject?projectName=` +
          data.projectName
        // Only updatedData is sent as the payload
      );
      /* console.log(response.data); */
      if (response.data) {
        // Correctly get the state
        const state = getState() as RootState;

        // Find the index of the existing project in Redux store
        const existingProjectIndex = state.project.projects.findIndex(
          (project) => project.name === data.projectName
        );
        const downloadedProjectIndex = response.data.findIndex(
          (latest: { name: string }) => latest.name === data.projectName
        );

        if (existingProjectIndex !== -1) {
          // Create a copy of the projects array
          const updatedProjects = [...state.project.projects];
          // Update only the found project
          updatedProjects[existingProjectIndex] = {
            ...updatedProjects[existingProjectIndex],
            ...response.data[downloadedProjectIndex], // Merge new data
          };
          // Remove the isNotDownload property if it exists
          delete updatedProjects[existingProjectIndex].isNotDownload;
          //console.log("Updated Projects List:", updatedProjects[existingProjectIndex]);
          return updatedProjects; // Return the updated list
        }
      }
      return response.data;
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

const ProjectSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    toggleLikedById: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const index = state.projects.findIndex((proj) => proj.id === id);
      if (index !== -1) {
        state.projects[index].liked = !state.projects[index].liked;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.status = "LoadingProjects";
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.status = SUCCEEDED;
        // Ensure each project has a "category" field
        state.projects = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.status = FAILED;
        state.error = action.error.message || FAILED_TO_FETCH_PROJECTS;
      })
      .addCase(addProjectAPI.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(addProjectAPI.fulfilled, (state, action) => {
        state.status = SUCCEEDED;
        state.projects.push(action.payload);
      })
      .addCase(addProjectAPI.rejected, (state, action) => {
        state.status = FAILED;
        state.error = action.error.message || FAILED_TO_ADD_PROJECT;
      })
      .addCase(editProjectAPI.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(editProjectAPI.fulfilled, (state, action) => {
        state.status = SUCCEEDED;
        const { id, updatedData } = action.payload;
        const index = state.projects.findIndex(
          (proj) => proj.id === id.toString()
        );
        if (index !== -1) {
          state.projects[index] = { ...state.projects[index], ...updatedData };
        }
      })
      .addCase(editProjectAPI.rejected, (state, action) => {
        state.status = FAILED;
        state.error = action.error.message || "Failed to edit project";
      })
      .addCase(toggleLikeAPI.pending, (state, action) => {
        // Optionally handle pending state
      })
      .addCase(toggleLikeAPI.fulfilled, (state, action) => {
        state.status = SUCCEEDED;
        const { id, liked } = action.payload;
        const index = state.projects.findIndex((proj) => proj.id === id);
        if (index !== -1) {
          state.projects[index].liked = liked;
        }
      })
      .addCase(toggleLikeAPI.rejected, (state, action) => {
        state.status = FAILED;
        state.error = action.error.message || FAILED_TO_EDIT_PROJECT;
      })
      // Handle addProject cases
      .addCase(addProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects.push(action.payload); //change from task to project
      })
      .addCase(addProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string; // Handle error
      })
      // Handle downloadSample Project
      .addCase(downloadSampleProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadSampleProject.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.projects = action.payload; // ✅ Replace only updated projects
        }
      })
      .addCase(downloadSampleProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string; // Handle error
      })
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action: any) => {
        state.loading = false;
        state.projects = state.projects.filter(
          (project) => project.id !== action.payload
        ); // Remove the deleted project
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { toggleLikedById } = ProjectSlice.actions;

export default ProjectSlice.reducer;

export const selectProjectById = (state: RootState, id: string) =>
  state.project.projects.find((proj) => proj.id === id);
